/**
 * Subdomain and cross-domain URL helper utilities.
 * Supports both production (harivishnu.in) and local development (*.localhost).
 */

export type SubdomainType = 'blog' | 'podcast' | 'store' | 'apps' | '';

/**
 * Checks if the current environment is running locally (localhost, 127.0.0.1, *.localhost).
 */
export function isLocalhost(): boolean {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;
    return (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.endsWith('.localhost') ||
        hostname.endsWith('.local')
    );
}

/**
 * Returns the current active subdomain ('blog', 'podcast', 'store', 'apps', or '').
 */
export function getSubdomain(hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''): SubdomainType {
    if (!hostname) return '';

    // Store
    if (
        hostname === 'store.harivishnu.in' ||
        hostname === 'www.store.harivishnu.in' ||
        hostname === 'store.localhost' ||
        hostname.startsWith('store.')
    ) {
        return 'store';
    }

    // Blog
    if (
        hostname === 'blog.harivishnu.in' ||
        hostname === 'www.blog.harivishnu.in' ||
        hostname === 'blog.localhost' ||
        hostname.startsWith('blog.')
    ) {
        return 'blog';
    }

    // Podcast
    if (
        hostname === 'podcast.harivishnu.in' ||
        hostname === 'www.podcast.harivishnu.in' ||
        hostname === 'podcast.localhost' ||
        hostname.startsWith('podcast.')
    ) {
        return 'podcast';
    }

    // Apps
    if (
        hostname === 'apps.harivishnu.in' ||
        hostname === 'www.apps.harivishnu.in' ||
        hostname === 'apps.localhost' ||
        hostname.startsWith('apps.')
    ) {
        return 'apps';
    }

    return '';
}

/**
 * Generates an absolute URL for a given subdomain and path.
 * In local development, formats as `http://{subdomain}.localhost:{port}{path}`.
 * In production, formats as `https://{subdomain}.harivishnu.in{path}`.
 */
export function getSubdomainUrl(subdomain: SubdomainType, path: string = '/'): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;

    if (typeof window === 'undefined') {
        if (!subdomain) return `https://www.harivishnu.in${cleanPath}`;
        return `https://${subdomain}.harivishnu.in${cleanPath}`;
    }

    const port = window.location.port ? `:${window.location.port}` : '';
    const protocol = window.location.protocol || 'http:';

    if (isLocalhost()) {
        if (!subdomain) {
            return `${protocol}//localhost${port}${cleanPath}`;
        }
        return `${protocol}//${subdomain}.localhost${port}${cleanPath}`;
    }

    // Production
    if (!subdomain) {
        return `https://www.harivishnu.in${cleanPath}`;
    }
    return `https://${subdomain}.harivishnu.in${cleanPath}`;
}

export function getMainSiteUrl(path: string = '/'): string {
    return getSubdomainUrl('', path);
}

export function getBlogUrl(path: string = '/'): string {
    return getSubdomainUrl('blog', path);
}

export function getPodcastUrl(path: string = '/'): string {
    return getSubdomainUrl('podcast', path);
}

export function getStoreUrl(path: string = '/'): string {
    return getSubdomainUrl('store', path);
}

export function getAppsUrl(path: string = '/'): string {
    return getSubdomainUrl('apps', path);
}
